import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import type { Cue, Track } from './cues.js'
import { createPlayer, silentPlayer, type PlayerOptions, type Sounds } from './player.js'
import { SoundContext, type SoundChoice } from './sound-context.js'
import { isTyping } from '../games/useGameKeys.js'

const STORAGE_KEY = 'hg_sound'

/**
 * On unless she has said otherwise.
 *
 * A game with no sound is what a child would notice first, so the default is
 * the one nobody has to discover. It is a switch away, and remembered.
 */
function storedChoice(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== 'off'
  } catch {
    // A browser with storage refused still gets sound; it just forgets.
    return true
  }
}

function remember(on: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off')
  } catch {
    // Nothing to be done, and nothing worth saying about it.
  }
}

export interface SoundProviderProps {
  children: ReactNode
  /**
   * Whether this screen is one that makes a noise.
   *
   * The television is; nothing else is. Passed in rather than read here, so
   * the rule lives with the layout that knows about scales and this file stays
   * about sound.
   */
  heard: boolean
  /** For tests, which have no audio and want to see what was asked for. */
  options?: PlayerOptions
}

export function SoundProvider({ children, heard, options }: SoundProviderProps) {
  const [on, setOnState] = useState(storedChoice)

  /*
   * Built once, and only where anything can be heard.
   *
   * A phone gets `silentPlayer`, which is not "the player with the volume at
   * zero" — it never makes an audio element, never fetches a file, never
   * decodes anything. Eight tracks it will never play should cost it nothing,
   * not even the download.
   */
  const player = useRef<Sounds | null>(null)
  if (player.current === null) {
    player.current = heard ? createPlayer(options) : silentPlayer()
  }

  /*
   * The scale can change under us — the profile page has a switch for it — and
   * a silent player would then stay silent on a television.
   */
  const wasHeard = useRef(heard)
  useEffect(() => {
    if (wasHeard.current === heard) return

    wasHeard.current = heard
    player.current?.stop()
    player.current = heard ? createPlayer(options) : silentPlayer()
  }, [heard, options])

  /*
   * A provider takes its noise with it when it goes.
   *
   * There are two of these — `AppLayout` has one and `TvLayout` has another —
   * so walking from the menu into a room is not a navigation inside one
   * provider. It unmounts one and mounts the other, with a player each.
   *
   * Without this, the leaving provider's loop simply carried on, out of a
   * detached element nobody held a reference to any more: the menu's music
   * followed her into the room, and coming back built a second player, asked
   * it for the menu track, and started a second copy over the first. One more
   * every trip, and a trip she makes over and over.
   *
   * Stopped rather than discarded. React's development double-invoke unmounts
   * and remounts every effect, and the player is built during *render* — so
   * clearing the reference here would leave nothing to rebuild it and kill the
   * sound outright in development. Stopping is idempotent; the screens ask for
   * their music again as their own effects re-run.
   */
  useEffect(() => {
    const sounds = player
    return () => sounds.current?.stop()
  }, [])

  /*
   * Nothing plays until somebody has pressed something.
   *
   * Every browser refuses before that, and the Pi boots straight into the menu
   * with nobody having pressed anything — so this is the ordinary case, not an
   * error. Both a key and a touch count, and once is enough for the life of
   * the page.
   */
  // Attached before the paint, like every other window listener in this app:
  // a passive effect runs after the browser has drawn, and the very first key
  // somebody presses is the one that would fall into that gap — which here is
  // the key that lets any sound play at all.
  useLayoutEffect(() => {
    if (!heard) return

    function wake(): void {
      player.current?.unlock()
    }

    window.addEventListener('keydown', wake, { once: true })
    window.addEventListener('pointerdown', wake, { once: true })

    return () => {
      window.removeEventListener('keydown', wake)
      window.removeEventListener('pointerdown', wake)
    }
  }, [heard])

  /*
   * Off from the sofa, on any screen, with one key.
   *
   * `#` because it is the only thing free everywhere. Hoppity's guesses every
   * letter of the alphabet and Small Rabbit's takes a tile with one, so there
   * is no letter to have — M was tried and had to live on the menu alone,
   * which meant Escape first and no way to silence a game while it played.
   * The digits belong to Monkey's, Turtley's, Rocky's and both pickers; the
   * arrows, Enter, Space, Backspace and Escape are all spoken for, and
   * Scramble has taken `?` and `/`.
   *
   * On the keyboard under the television it is a key of its own beside Enter.
   * On a US layout it is Shift+3, which arrives here as `#` just the same,
   * because a shift is not one of the modifiers this steps aside for.
   *
   * Here rather than in a screen, so it works in every game — and beside the
   * legend entry in `KeyLegend`, so the offer and the key cannot come apart.
   */
  useLayoutEffect(() => {
    if (!heard) return

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== '#') return
      if (event.metaKey || event.ctrlKey || event.altKey) return
      // Somebody typing a name into a box gets the character they pressed.
      if (isTyping(event.target)) return

      event.preventDefault()
      setOnState((was) => {
        const next = !was
        remember(next)
        if (!next) player.current?.stop()
        return next
      })
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [heard])

  const setOn = useCallback((next: boolean) => {
    setOnState(next)
    remember(next)
    if (!next) player.current?.stop()
  }, [])

  const play = useCallback(
    (cue: Cue) => {
      if (on) player.current?.play(cue)
    },
    [on],
  )

  /*
   * What the screen last asked for, kept so switching sound back on starts the
   * music of the screen she is looking at rather than silence until she moves.
   */
  const asked = useRef<Track | null>(null)

  const music = useCallback(
    (track: Track | null) => {
      asked.current = track
      player.current?.music(on ? track : null)
    },
    [on],
  )

  useEffect(() => {
    player.current?.music(on ? asked.current : null)
  }, [on])

  const value = useMemo<SoundChoice>(
    () => ({ play, music, on, setOn, heard }),
    [play, music, on, setOn, heard],
  )

  return <SoundContext.Provider value={value}>{children}</SoundContext.Provider>
}
